import React, { useState } from 'react';
import { Box, Typography, Button, Avatar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ICompanyDetails } from '../../redux/company/types';
import { ReviewFormModal } from './ReviewFormModal';
import { useSelector } from 'react-redux';
import { userProfileSelector } from '../../selectors/selectors';
import { Edit, Star } from 'lucide-react';
import { useAuthModal } from '../../module/AuthenticationModule';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface ICompanyHeader {
    companyDetails?: ICompanyDetails;
    onEditClick?: () => void;
}

const CompanyHeader: React.FC<ICompanyHeader> = ({ companyDetails, onEditClick }) => {
    const { themeStyle, device } = useThemeContext();
    const muiTheme = useTheme();
    /** Nest Hub / short landscape: row layout leaves almost no width for the title beside actions. */
    const isShortViewport = useMediaQuery('(max-height: 720px)');
    /** iPad portrait & sub-lg widths: use stacked hero so the name row keeps full width (lg = 1200). */
    const isWideEnoughForHeroRow = useMediaQuery(muiTheme.breakpoints.up('lg'));
    const useSideBySideHero = !isShortViewport && isWideEnoughForHeroRow;
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const currentUser = useSelector(userProfileSelector);
    const { withAuth } = useAuthModal();

    const isCompanyCreator = currentUser?._id === companyDetails?.company?.created_by?._id;
    const c = companyDetails?.company;
    const showEditCompany = Boolean(isCompanyCreator && onEditClick);

    const heroImage = (c?.gallery && c.gallery.length > 0 && c.gallery[0]) || c?.social_thumbnail || c?.profile_picture;

    const pillButtonSx = {
        borderRadius: 9999,
        px: 3,
        py: 1.5,
        textTransform: 'none' as const,
        fontFamily: FONT_ENUM.MEDIUM_TEXT,
        fontWeight: 600,
        fontSize: '0.9375rem',
        borderColor: 'rgba(255,255,255,0.12)',
        color: theme[themeStyle].tag_white,
        bgcolor: '#262626',
        '&:hover': {
            bgcolor: '#2c2c2c',
            borderColor: 'rgba(255,255,255,0.2)',
        },
    };

    /** Matches ThemeManager: true when viewport width ≤ 767 (mobile mode only for hero treatment). */
    const isMobileHero = device.isMobile;

    const pillButtonSxMobile = {
        ...pillButtonSx,
        px: 1.25,
        py: 0.65,
        fontSize: '0.6875rem',
        fontWeight: 600,
        minHeight: 34,
        '& .MuiButton-startIcon': { mr: 0.5, '& svg': { width: 13, height: 13 } },
    };

    const pageBase = theme[themeStyle].pure_black;
    const immersiveMinHeight = isShortViewport ? { xs: 260, md: 280 } : { xs: 320, md: 420 };

    /** Full-bleed hero: same fade pattern as audition `CoverImageRenderer` (immersive). */
    const heroShellSx = heroImage
        ? {
              position: 'relative' as const,
              width: '100%',
              maxWidth: '100%',
              minHeight: immersiveMinHeight,
              borderRadius: 0,
              overflow: 'hidden',
              backgroundImage: [
                  `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 48px), ${pageBase} 100%)`,
                  `linear-gradient(to top, rgba(14, 14, 14, 0.92) 0%, rgba(14, 14, 14, 0.45) 45%, transparent 72%)`,
                  `linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.12))`,
                  `url(${heroImage})`,
              ].join(', '),
              backgroundSize: 'cover',
              backgroundPosition: { xs: 'center top', sm: 'center center' },
              backgroundRepeat: 'no-repeat',
          }
        : {
              position: 'relative' as const,
              width: '100%',
              maxWidth: '100%',
              minHeight: immersiveMinHeight,
              borderRadius: 0,
              overflow: 'hidden',
              background: `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 48px), ${pageBase} 100%), #131313`,
          };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                alignSelf: 'stretch',
                mb: 0,
            }}>
            <Box sx={heroShellSx}>
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        p: { xs: 2.5, sm: 3, md: 4 },
                        '@media (max-width: 360px)': {
                            px: 2,
                            py: 2,
                        },
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: { xs: 560, md: '100%' },
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: useSideBySideHero ? { xs: 'column', lg: 'row' } : 'column',
                        alignItems: useSideBySideHero ? { xs: 'stretch', lg: 'flex-end' } : 'stretch',
                        justifyContent: { xs: 'flex-end', sm: 'space-between' },
                        gap: 1.5,
                        minHeight: immersiveMinHeight,
                        ...(isMobileHero && {
                            justifyContent: 'flex-end',
                            alignItems: 'flex-start',
                            maxWidth: '100%',
                            mx: 0,
                        }),
                        '@media (min-width: 768px) and (max-width: 1024px)': {
                            mx: 0,
                            maxWidth: '100%',
                        },
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: { xs: 'flex-end', sm: 'flex-end' },
                            gap: 2,
                            flex: 1,
                            minWidth: 0,
                            mb: { xs: 0.5, sm: 0 },
                            ...(isMobileHero && {
                                mt: 'auto',
                                mb: 1.25,
                                width: '100%',
                            }),
                        }}>
                        <Avatar
                            src={c?.profile_picture}
                            sx={{
                                width: { xs: 56, sm: 80 },
                                height: { xs: 56, sm: 80 },
                                borderRadius: 2,
                                border: '1px solid rgba(255,255,255,0.1)',
                                bgcolor: '#262626',
                                fontSize: { xs: '1.4rem', sm: '2rem' },
                                fontWeight: 800,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            {c?.company_name?.[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1, maxWidth: '100%' }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: '1.25rem', sm: '2.25rem', md: '2.75rem' },
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    mb: { xs: 0.5, sm: 1 },
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}>
                                {c?.company_name}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,0.55)',
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                    fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
                                    textTransform: 'capitalize',
                                    mb: { xs: 0.9, sm: 1.5 },
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}>
                                {c?.company_type}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.4,
                                        borderRadius: 9999,
                                        border: `1px solid ${COMPANY_BRAND_ORANGE}4d`,
                                        bgcolor: `${COMPANY_BRAND_ORANGE}33`,
                                    }}>
                                    <Star size={14} fill={COMPANY_BRAND_ORANGE} color={COMPANY_BRAND_ORANGE} />
                                    <Typography
                                        sx={{
                                            color: COMPANY_BRAND_ORANGE,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        }}>
                                        {c?.rating != null ? c.rating.toFixed(1) : '—'}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.45)',
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                        letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                        textTransform: 'uppercase',
                                    }}>
                                    {c?.number_of_reviews ?? 0} reviews
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                            gap: 1.5,
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: useSideBySideHero ? { xs: 'flex-start', lg: 'flex-end' } : 'flex-start',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            width: useSideBySideHero ? undefined : '100%',
                            ...(isMobileHero && {
                                flexDirection: 'row',
                                flexWrap: 'nowrap',
                                gap: 1,
                                width: '100%',
                                maxWidth: '100%',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                            }),
                        }}>
                        {showEditCompany && (
                            <Button
                                variant="outlined"
                                startIcon={<Edit size={isMobileHero ? 13 : 16} />}
                                onClick={onEditClick}
                                fullWidth={!isMobileHero}
                                sx={{
                                    ...(isMobileHero ? pillButtonSxMobile : pillButtonSx),
                                    width: isMobileHero ? 'auto' : { xs: '100%', sm: 'auto' },
                                    minHeight: isMobileHero ? 34 : { xs: 44, sm: 'unset' },
                                    flex: isMobileHero && showEditCompany ? 1 : undefined,
                                    minWidth: 0,
                                    fontSize: isMobileHero ? '0.6875rem' : { xs: '0.875rem', sm: pillButtonSx.fontSize },
                                }}>
                                Edit Company
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={withAuth(() => setIsReviewModalOpen(true))}
                            fullWidth={!isMobileHero}
                            sx={{
                                ...(isMobileHero ? pillButtonSxMobile : pillButtonSx),
                                width: isMobileHero ? 'auto' : { xs: '100%', sm: 'auto' },
                                minHeight: isMobileHero ? 34 : { xs: 44, sm: 'unset' },
                                flex: isMobileHero && showEditCompany ? 1 : undefined,
                                minWidth: 0,
                                fontSize: isMobileHero ? '0.6875rem' : { xs: '0.875rem', sm: pillButtonSx.fontSize },
                            }}>
                            Leave a review
                        </Button>
                    </Box>
                </Box>
            </Box>

            <ReviewFormModal
                open={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                companyId={companyDetails?.company._id || ''}
                companyName={companyDetails?.company.company_name || ''}
            />
        </Box>
    );
};

export { CompanyHeader };
